import React, { useState, useEffect } from "react";
import withStyles from "isomorphic-style-loader/withStyles";
import cx from "classnames";
import { FormattedMessage } from "react-intl";
import { useDispatch, useSelector } from "react-redux";
import { ElementsConsumer } from "@stripe/react-stripe-js";
import { formValueSelector } from "redux-form";

import Link from "../Link/Link";
import ProviderDetails from "../ProviderDetails/ProviderDetails";
import OrderDetails from "../BookingDetail/OrderDetails/OrderDetails";
import PriceSplitUp from "../BookingDetail/PriceDetails/PriceSplitUp/PriceSplitUp";
import PaymentMethod from "../BookingDetail/PriceDetails/PaymentMethod/PaymentMethod";
import ProceedPaymentBanner from "../ProceedPaymentBanner/ProceedPaymentBanner";
import ViewBookingSkeleton from "../Skeleton/ViewBooking/ViewBookingSkeleton/ViewBookingSkeleton";

import history from "../../history";
import messages from "../../locale/messages";
import { siteUrl } from "../../config";
import { getBooking } from "../../actions/booking/getBooking";
import { addTipsAmountAction } from "../../actions/booking/addTipsAmountAction";

import useQueryResponse from "../../lib/hooks/useQueryResponse";
import getBookingById from './getBookingById.graphql';
import getPaymentMethod from './getPaymentMethod.graphql';
import { getAllPaymentMethodsQuery } from "../../lib/graphql";

import { AngleLeftArrowIcon, PrintIcon } from "../Common/Assets/SVGIcon/CommonSvgIcon";
import tipIcon from '/public/Icons/tipIcon.svg';
import promoDeleteIcon from '/public/dashboard/promoDeleteIcon.svg';

import bt from '../../components/commonStyle.css';
import s from './BillingDetails.css';




const BillingDetails = ({ data, id, socket, isTipAmount }) => {

    const [load, setLoad] = useState(true)
    const dispatch = useDispatch()
    const paymentMethodId = useSelector(state => formValueSelector('PaymentMethod')(state, 'paymentMethodId'))
    const paymentCurrency = useSelector(state => formValueSelector('PaymentMethod')(state, 'paypalCurrency'))

    useEffect(() => {
        async function fetchData() {
            if (socket && id) {
                socket.on(`bookingStatusUpdated-${id}`, async (value) => {
                    await dispatch(getBooking(id));
                });
            }
        }
        fetchData()
        setLoad(false)
    }, [])

    const { data: getBookingDetails, loading, refetch } = useQueryResponse({
        query: getBookingById,
        variables: {
            bookingId: id
        },
        fetchPolicy: 'network-only',
        ssr: false
    })
    const { data: userPaymentDetails, loading: paymentLoading } = useQueryResponse({
        query: getPaymentMethod,
        fetchPolicy: 'network-only',
        ssr: false
    })
    const { data: paymentMethods, loading: paymentMethodLoading } = useQueryResponse({
        query: getAllPaymentMethodsQuery,
        ssr: true,
        fetchPolicy: 'network-only'
    })

    const handleDownloadPDF = (event) => {
        event.preventDefault();
        const downloadUrl = `${siteUrl}/download-pdf?orderId=${getBookingDetails?.getBookingById?.result?.orderId}&userType=1`;
        window.open(downloadUrl, '_blank');
    };

    const handleRemove = async () => {
        let paymentType = getBookingDetails?.getBookingById?.result?.paymentType, currency = getBookingById?.result?.currency;
        await dispatch(addTipsAmountAction({ bookingId: id, currency, amount: 0, paymentType }))
    }

    let categoryName, userServiceFee, userPayableFare, travellingPrice, specialServiceFare, currency, tipsAmount;
    const redirectTo = history?.location?.state?.from ? history?.location?.state?.from : '/dashboard';
    if (getBookingDetails?.getBookingById?.result) {
        categoryName = getBookingDetails?.getBookingById?.result?.categoryDetails?.name;
        ({ userServiceFee, travellingPrice, userPayableFare, specialServiceFare, currency, tipsAmount } = getBookingDetails?.getBookingById?.result);
    }
    let initialValues = {
        bookingId: id
    };

    const enabledPaymentMethods =
        paymentMethods?.getAllPaymentMethods?.results?.filter(
            method => method.isEnable && method.paymentType !== 4
        ) || [];
    const isUserPaymentMethodEnabled = enabledPaymentMethods?.some(
        method => method.paymentType === getBookingDetails?.getBookingById?.result?.paymentType
    );
    if (!loading) {
        if (isUserPaymentMethodEnabled) {
            initialValues = {
                paymentMethodId: String(getBookingDetails?.getBookingById?.result?.paymentType) || '',
            };
        } else if (enabledPaymentMethods?.length > 0) {
            initialValues = { paymentMethodId: enabledPaymentMethods[0].paymentType };
        } else {
            initialValues = { paymentMethodId: '' };
        }

    }
    if (load) {
        return <ViewBookingSkeleton />
    }
    return (
        <>
            <>
                <div className={cx(bt.space3, s.billingTopLinkGrp)}>
                    <a href={redirectTo}
                        onClick={(e) => {
                            e.preventDefault();
                            history.push({ pathname: redirectTo, state: { from: history.location.pathname } });
                        }}
                        className={cx(bt.customLink, 'customLinkRtl')}
                    >
                        <AngleLeftArrowIcon />
                        <FormattedMessage {...messages.goBackText} />
                    </a>
                    <Link to={''} onClick={(event) => handleDownloadPDF(event)} className={bt.customLink}>
                        <PrintIcon />
                        <FormattedMessage {...messages.printBooking} />
                    </Link>
                </div>
                <div className={bt.commonViewBookingContainer}>
                    <div className={bt.commonViewBookingLsBox}>
                        <OrderDetails data={getBookingDetails?.getBookingById} isBillingPage={true} isBooking id={id} refetch={refetch} />
                    </div>
                    <div className={bt.commonStickyRsContainer}>
                        <div className={cx(bt.commonViewBookingRsBox, s.billingIdCardTopRadius)}>
                            <div className={s.bookingIdCardWrapper}>
                                <div className={s.bookingIdInfoIdBox}>
                                    <span className={bt.displayInlineBlock}><FormattedMessage {...messages.idText} /></span>
                                    <span className={bt.displayInlineBlock}>&nbsp;#{id}</span>
                                </div>
                                <div className={cx(bt.discountForeground, bt.commonSize16, bt.fontWeight500)}>
                                    <FormattedMessage {...messages.completed} />
                                </div>
                            </div>
                        </div>
                        <ProviderDetails data={getBookingDetails?.getBookingById?.result} isNotMapPage={true} />
                        <PriceSplitUp
                            data={getBookingDetails?.getBookingById}
                            categoryName={categoryName}
                            serviceFee={userServiceFee}
                            visitFee={travellingPrice}
                            total={userPayableFare}
                            discount={specialServiceFare}
                            currency={currency}
                            fee={getBookingDetails?.getBookingById?.result?.additionalFee}
                            isTipAmount={isTipAmount}
                            paymentType={getBookingDetails?.getBookingById?.result?.paymentType}
                            tipsAmount={tipsAmount}
                            isPaymentCompleted={true}
                        />
                        {
                            tipsAmount > 0 && <div className={cx(bt.commonViewBookingRsBox, bt.spaceTop1, bt.space1)}>
                                <div className={cx(s.priceDetailsFlex, 'svgImg')}>
                                    <div className={s.promoCodeLsInfo}>
                                        <img src={tipIcon} alt="promoCodeIcon" />
                                        <span className={cx(bt.commonSize16, bt.fontWeight400, bt.primaryColor)}>
                                            <FormattedMessage {...messages.tipsAdded} />
                                        </span>
                                    </div>
                                    <img src={promoDeleteIcon} alt="promoDeleteIcon" onClick={() => handleRemove()} className={cx(bt.cursorPointer)} />
                                </div>
                            </div>
                        }
                        <PaymentMethod id={id} initialValues={initialValues} userPaymentMethod={userPaymentDetails} isaddPayment />
                    </div>
                </div>
            </>
            <ElementsConsumer>
                {({ stripe, elements }) => (
                    <ProceedPaymentBanner id={id} paymentMethodId={paymentMethodId} stripe={stripe} elements={elements} paymentCurrency={paymentCurrency} />
                )}
            </ElementsConsumer>
        </ >
    )
}

export default withStyles(bt, s)(BillingDetails);
